import { useMutation } from "@tanstack/react-query";
import z from "zod/v4";

import {
  DelegationMetadataSchema,
  UserPreferencesWithSubaccountsResponseDataSchema,
} from "@/contexts/queries/user-preferences";
import { env, isProduction } from "@/env";
import { isFetchErrorWithStatus } from "@/utils/error";
import { Logger } from "@/utils/logger";
import {
  authenticatedRequest,
  AuthNotReadyError,
  type GetAuthHeaders,
} from "@/utils/signature-auth";

const DelegationsSchema = z.record(z.string(), DelegationMetadataSchema);

export type DelegationMetadata = z.infer<typeof DelegationMetadataSchema>;

export type DelegationsRecord = z.infer<typeof DelegationsSchema>;

const UpdateUserPreferencesRequestBodySchema = z.object({
  customSubaccountNames: z.record(z.string(), z.string()).optional(),
  maxSlippage: z.number().optional(),
  watchlist: z.array(z.string()).optional(),
  tradingMode: z.enum(["basic", "advanced"]).optional(),
  marketCloseConfirmation: z.boolean().optional(),
  placeOrderConfirmation: z.boolean().optional(),
  delegations: DelegationsSchema.optional(),
  subaccountAddress: z.string().optional(),
});

export type UpdateUserPreferencesRequestBody = z.infer<
  typeof UpdateUserPreferencesRequestBodySchema
>;

export interface UpdateUserPreferencesProps {
  accountAddress: string;
  subaccountAddress?: string;
  preferences: UpdateUserPreferencesRequestBody;
}

export const UpdateUserPreferencesResponseSchema = z.object({
  success: z.boolean(),
  data: UserPreferencesWithSubaccountsResponseDataSchema.optional(),
  message: z.string().optional(),
  error: z.string().optional(),
});

const UPDATE_USER_PREFERENCES_URL = `${env.NEXT_PUBLIC_API_URL}/api/user-preferences`;

export function useUpdateUserPreferences({
  getAuthHeaders,
}: {
  getAuthHeaders: GetAuthHeaders | undefined;
}) {
  return useMutation({
    mutationFn: async ({ preferences, subaccountAddress }: UpdateUserPreferencesProps) => {
      try {
        UpdateUserPreferencesRequestBodySchema.parse(preferences);
        const body = { ...preferences };
        if (subaccountAddress) body.subaccountAddress = subaccountAddress;

        const response = await authenticatedRequest.post({
          schema: UpdateUserPreferencesResponseSchema,
          url: UPDATE_USER_PREFERENCES_URL,
          body,
          getAuthHeaders,
          subaccountAddress,
        });

        return response.data.data?.userPreferences;
      } catch (error) {
        // Hydration race; wrapper already logged. makeUpdateFn runs
        // applyLocalPreference outside its try/catch, so the toggle still
        // sticks in the Zustand store.
        if (error instanceof AuthNotReadyError) return;
        // 401 = SIWA/session lifecycle (expired message, nonce replay, delegation
        // expired). Expected churn, warn not error.
        // Note: 403 is NOT suppressed — server returns it only when the target
        // subaccount isn't owned by the authed account, which is either a UI bug
        // or an attack and worth paging on.
        if (isFetchErrorWithStatus(error, [401])) {
          Logger.warn("user-preferences POST 401 (auth lifecycle)", {
            subaccountAddress,
          });
          return;
        }
        if (isProduction) {
          Logger.error(error, {
            message: "Failed to update user preferences",
            subaccountAddress,
          });
        } else {
          Logger.warn("user-preferences-api is not accessible");
        }
      }
    },
  });
}
